import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const DATA_FILE_PATH = "/uploads/data.json";

const slugify = (text) => {
    if (!text) return '';
    return text
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = [];
    let lastIndex = 0;
    // Use 'gi' for global and case-insensitive search
    const regex = new RegExp(`(${query})`, 'gi'); 

    text.replace(regex, (match, p1, offset) => {
        if (offset > lastIndex) {
            parts.push(text.substring(lastIndex, offset));
        }
        // Use a unique key for React
        parts.push(<span key={offset} className="bg-yellow-300 text-black">{match}</span>);
        lastIndex = offset + match.length;
        return match;
    });

    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    return parts;
};

export default function Header() {
    const [fullData, setFullData] = useState({ Brands: [], Industries: [], Categories: [], Playables: [] });
    const [isLoading, setIsLoading] = useState(true);

    const [industriesOpen, setIndustriesOpen] = useState(false);
    const industriesRef = useRef(null);
    const [gameCategoriesOpen, setgameCategoriesOpen] = useState(false);
    const gameCategorieRef = useRef(null);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    
    const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
    const desktopSearchRef = useRef(null);
    // 1. New Ref for desktop search results container
    const desktopResultsRef = useRef(null); 
    
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const mobileSearchRef = useRef(null); 
    // 2. New Ref for mobile search results container
    const mobileResultsRef = useRef(null); 

    useEffect(() => {
        fetch(DATA_FILE_PATH)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch data: ${response.statusText}`);
                }
                return response.json();
            })
            .then(fetchedData => {
                setFullData(fetchedData);
            })
            .catch(err => {
                console.error("Error loading header data:", err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // 3. Effect to handle scrolling when focusedIndex changes
    useEffect(() => {
        if (focusedIndex >= 0) {
            let containerRef = desktopSearchOpen ? desktopResultsRef : mobileResultsRef;
            
            if (containerRef.current) {
                // The children of the container are the actual search result links/items
                const focusedElement = containerRef.current.children[focusedIndex + 1]; // +1 to skip the header element
                
                if (focusedElement) {
                    focusedElement.scrollIntoView({
                        block: 'nearest', 
                        inline: 'nearest'
                    });
                }
            }
        }
    }, [focusedIndex, desktopSearchOpen, mobileSearchOpen]);

    const createResult = (name, type, path, slugModifier = '', subtitle = '', playableId = null) => {
        let url;

        if (type === 'Brand' && slugModifier) {
            url = `/industries/${slugify(slugModifier)}/${slugify(name)}`;
        } else if (type === 'Playable' && playableId) {
            url = `/playable/${playableId}`;
        } else {
            url = `/${path}/${slugify(name)}`;
        }
        
        return {
            name,
            type,
            url: url,
            key: `${name}-${type}-${playableId || slugify(name)}`,
            subtitle
        };
    };
    
    const handleResultClick = () => {
        setSearchTerm("");
        setSearchResults([]);
        setFocusedIndex(-1);
        setDesktopSearchOpen(false);
        setMobileSearchOpen(false);
        setMobileMenuOpen(false);
    };

    const updateSearchResults = (value) => {
        const query = value.toLowerCase().trim();
        let results = [];
        const addedKeys = new Set();
        
        if (value.length === 0) {
            setSearchResults([]);
            setFocusedIndex(-1);
            return;
        }

        const addResult = (result) => {
            if (!addedKeys.has(result.key)) {
                results.push(result);
                addedKeys.add(result.key);
            }
        };
        
        // Helper to check for a match in name or type
        const matchesQuery = (item, typeName) => {
            const nameMatch = item.name.toLowerCase().includes(query);
            const typeMatch = typeName.toLowerCase().startsWith(query);
            return nameMatch || typeMatch;
        };

        const industriesToDisplay = fullData.Industries.filter(industry => matchesQuery(industry, 'industries'));
        industriesToDisplay.forEach(industry => addResult(createResult(industry.name, 'Industry', 'industries')));

        const brandsToDisplay = fullData.Brands.filter(brand => matchesQuery(brand, 'brands'));
        brandsToDisplay.forEach(brand => addResult(createResult(brand.name, 'Brand', 'industries', brand.industry, brand.industry)));

        const playablesToDisplay = fullData.Playables.filter(playable => matchesQuery(playable, 'playables'));
        playablesToDisplay.forEach(playable => addResult(createResult(playable.name, 'Playable', 'playable', '', playable.type, playable.id)));

        const categoriesToDisplay = fullData.Categories.filter(category => matchesQuery(category, 'game categories'));
        categoriesToDisplay.forEach(category => addResult(createResult(category.name, 'Game Category', 'games')));
        
        setSearchResults(results.slice(0, 15));
        setFocusedIndex(-1);
    }
    
    const handleKeyDown = (event) => {
        if (!desktopSearchOpen && !mobileSearchOpen) return;
        if (searchResults.length === 0) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setFocusedIndex(prevIndex => 
                (prevIndex < searchResults.length - 1) ? prevIndex + 1 : 0
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setFocusedIndex(prevIndex => 
                (prevIndex > 0) ? prevIndex - 1 : searchResults.length - 1
            );
        } else if (event.key === 'Enter') {
            if (focusedIndex >= 0) {
                event.preventDefault();
                const result = searchResults[focusedIndex];
                window.location.href = result.url; 
                handleResultClick();
            }
        }
    };

    const handleDesktopSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        updateSearchResults(value);
        setDesktopSearchOpen(value.length > 0);
    };

    const handleMobileSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        updateSearchResults(value);
        setMobileSearchOpen(true); 
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileMenuOpen || mobileSearchOpen) return;

            if (industriesRef.current && !industriesRef.current.contains(event.target)) {
                setIndustriesOpen(false);
            }
            if (gameCategorieRef.current && !gameCategorieRef.current.contains(event.target)) {
                setgameCategoriesOpen(false);
            }
            
            if (
                desktopSearchRef.current &&
                !desktopSearchRef.current.contains(event.target) &&
                desktopSearchOpen
            ) {
                const isSearchIcon = event.target.closest('[data-testid="Search icon"]');
                if (!isSearchIcon) {
                    setDesktopSearchOpen(false);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [desktopSearchOpen, mobileMenuOpen, mobileSearchOpen]); 

    useEffect(() => {
        if (mobileMenuOpen || mobileSearchOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [mobileMenuOpen, mobileSearchOpen]);

    const industriesList = fullData.Industries.map(item => item.name);
    const gameCategoriesList = fullData.Categories.map(item => item.name);

    const inputClasses = "w-full py-2 pl-4 pr-10 bg-gray-900 text-white placeholder-gray-400 rounded-md focus:outline-none font-uber";
    
    const mobileInputClasses = inputClasses; 
    
    const mainLinkTextClasses = "font-uber font-bold text-[32px] leading-[40px]";
    const subLinkTextClasses = "font-uber font-normal text-[14px] leading-[20px] text-[#5E5E5E]";

    if (isLoading) {
        return (
            <nav className="sticky top-0 z-50 bg-black text-white shadow-md">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 h-[64px] flex items-center justify-between">
                    <p className="text-sm font-uber">Loading...</p>
                </div>
            </nav>
        );
    }

    return (
        <>
            <nav className="sticky top-0 z-50 bg-black text-white shadow-md">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 h-[64px] flex items-center justify-between">
                    <div className="hidden xl-custom:flex items-center justify-between w-full">
                        <div className="flex items-center space-x-10">
                            <Link to="/">
                                <img
                                    src="/uploads/Assets/logo.svg"
                                    alt="logo"
                                    className="h-6 w-auto cursor-pointer"
                                />
                            </Link>
                            <div className="flex space-x-4">
                                <div className="relative" ref={industriesRef}>
                                    <div
                                        className="cursor-pointer flex items-center px-4 py-2 whitespace-nowrap rounded-full transition-colors duration-200 hover:bg-gray-900 active:bg-gray-700"
                                        onClick={() => {
                                            setIndustriesOpen((prev) => !prev);
                                            setgameCategoriesOpen(false);
                                        }}
                                    >
                                        <span className="font-uber font-medium text-sm leading-4">Industries</span>
                                        <img
                                            src={industriesOpen ? "/uploads/Assets/up-arrow.svg" : "/uploads/Assets/Dropdown.svg"}
                                            alt="Dropdown Arrow"
                                            className="ml-2 h-3 w-3 mt-1"
                                        />
                                    </div>
                                    {industriesOpen && (
                                        <div className="absolute left-0 top-full mt-2 w-48 bg-white text-gray-600 shadow-lg rounded-md z-50 max-h-60 overflow-y-auto">
                                            {industriesList.map((industry, idx) => (
                                                <Link
                                                    to={`/industries/${slugify(industry)}`}
                                                    key={idx}
                                                    className="block px-4 py-2 hover:bg-gray-100 hover:text-black font-uber font-medium text-sm leading-4"
                                                    onClick={() => setIndustriesOpen(false)}
                                                >
                                                    {industry}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="relative" ref={gameCategorieRef}>
                                    <div
                                        className="cursor-pointer flex items-center px-4 py-2 whitespace-nowrap rounded-full transition-colors duration-200 hover:bg-gray-900 active:bg-gray-700"
                                        onClick={() => {
                                            setgameCategoriesOpen((prev) => !prev);
                                            setIndustriesOpen(false);
                                        }}
                                    >
                                        <span className="font-uber font-medium text-sm leading-4">Game Categories</span>
                                        <img
                                            src={gameCategoriesOpen ? "/uploads/Assets/up-arrow.svg" : "/uploads/Assets/Dropdown.svg"}
                                            alt="Dropdown Arrow"
                                            className="ml-2 h-3 w-3 mt-1"
                                        />
                                    </div>
                                    {gameCategoriesOpen && (
                                        <div className="absolute left-0 top-full mt-2 w-48 bg-white text-gray-600 shadow-lg rounded-md z-50 max-h-60 overflow-y-auto">
                                            {gameCategoriesList.map((item, idx) => (
                                                <Link
                                                    to={`/games/${slugify(item)}`}
                                                    key={idx}
                                                    className="block px-4 py-2 hover:bg-gray-100 hover:text-black font-uber font-medium text-sm leading-4"
                                                    onClick={() => setgameCategoriesOpen(false)}
                                                >
                                                    {item}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <Link
                                    to="/analytics"
                                    className="text-white cursor-pointer flex items-center px-4 py-2 rounded-full transition-colors duration-200 hover:bg-gray-900 active:bg-gray-700 whitespace-nowrap"
                                >
                                    <span className="font-uber font-medium text-sm leading-4">Analytics</span>
                                </Link>
                                <Link
                                    to="/case-study"
                                    className="text-white cursor-pointer flex items-center px-4 py-2 rounded-full transition-colors duration-200 hover:bg-gray-900 active:bg-gray-700 whitespace-nowrap"
                                >
                                    <span className="font-uber font-medium text-sm leading-4">Case Study</span>
                                </Link>
                            </div>
                        </div>
                        <div className="relative w-full max-w-xs" ref={desktopSearchRef}>
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleDesktopSearchChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => {
                                    searchTerm.length > 0 && setDesktopSearchOpen(true);
                                    setFocusedIndex(-1);
                                }}
                                className={inputClasses}
                            />
                            <img
                                src="/uploads/Assets/Search icon.svg"
                                alt="Search"
                                data-testid="Search icon"
                                className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-auto cursor-pointer"
                            />

                            {desktopSearchOpen && searchResults.length > 0 && (
                                <div 
                                    className="absolute right-0 top-full mt-2 w-72 bg-white text-gray-600 shadow-lg rounded-md z-50 max-h-80 overflow-y-auto border border-gray-200"
                                    ref={desktopResultsRef} 
                                >
                                    <p className="px-4 py-2 text-xs font-bold uppercase text-gray-500 border-b border-gray-100 font-uber">
                                        Search Results ({searchResults.length})
                                    </p>
                                    {searchResults.map((result, idx) => (
                                        <Link
                                            to={result.url}
                                            key={result.key}
                                            className={`block px-4 py-2 transition duration-150 font-uber ${
                                                focusedIndex === idx 
                                                    ? 'bg-gray-100 text-black' 
                                                    : 'hover:bg-gray-100 hover:text-black' 
                                            }`}
                                            onClick={handleResultClick}
                                        >
                                            <div className="font-uber font-medium text-sm leading-4 text-black">
                                                {highlightMatch(result.name, searchTerm)}
                                            </div>
                                            <div className="text-xs text-gray-500 font-uber">
                                                {result.type} 
                                                {(result.type === 'Brand' || result.type === 'Playable') && result.subtitle && (
                                                    <span className="ml-1 font-normal text-gray-400 font-uber">({result.subtitle})</span>
                                                )}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                            {desktopSearchOpen && searchResults.length === 0 && searchTerm.length > 0 && (
                                <div className="absolute right-0 top-full mt-2 w-72 bg-white text-gray-600 shadow-lg rounded-md z-50 p-4 border border-gray-200">
                                    <p className="text-sm text-gray-500 font-uber">No results found for "{searchTerm}".</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex xl-custom:hidden h-full items-center justify-between w-full relative">
                        <Link to="/">
                            <img
                                src="/uploads/Assets/logo.svg"
                                alt="logo"
                                className="h-6 w-auto cursor-pointer"
                            />
                        </Link>
                        <div className="flex items-center space-x-4">
                            <img
                                src="/uploads/Assets/Search icon.svg"
                                alt="Search"
                                className="h-5 w-auto cursor-pointer"
                                data-testid="Search icon"
                                onClick={() => {
                                    setMobileSearchOpen(true);
                                    setMobileMenuOpen(false);
                                    setSearchTerm("");
                                    setSearchResults([]);
                                }}
                            />
                            <img
                                src={mobileMenuOpen ? "/uploads/Assets/Close.svg" : "/uploads/Assets/Breadcrumbs.svg"}
                                alt="Menu Toggle"
                                className="h-5 w-5 cursor-pointer"
                                onClick={() => {
                                    if (mobileMenuOpen) {
                                        setIndustriesOpen(false);
                                        setgameCategoriesOpen(false);
                                    }
                                    setMobileMenuOpen(!mobileMenuOpen);
                                    setMobileSearchOpen(false);
                                }}
                            />
                        </div>
                    </div>
                </div>

                {mobileMenuOpen && (
                    <div className="xl-custom:hidden fixed inset-x-0 top-[64px] bottom-0 bg-white text-black z-40 p-6 flex flex-col space-y-4 overflow-y-auto">
                        <Link
                            to="/analytics"
                            className="text-black cursor-pointer py-1 font-uber font-bold text-[32px] leading-[40px]"
                            onClick={handleResultClick}
                        >
                            Analytics
                        </Link>

                        <Link
                            to="/case-study"
                            className={`cursor-pointer py-1 ${mainLinkTextClasses}`}
                            onClick={handleResultClick}
                        >
                            Case Study
                        </Link>

                        <div
                            className={`flex justify-between items-center cursor-pointer py-1`}
                            onClick={() => {
                                setIndustriesOpen((prev) => !prev);
                                setgameCategoriesOpen(false);
                            }}
                        >
                            <span className={mainLinkTextClasses}>Industries</span>
                            <img
                                src={industriesOpen ? "/uploads/Assets/uparrowblack.svg" : "/uploads/Assets/Downarrowblack.svg"}
                                alt="Arrow"
                                className="h-3 w-3"
                            />
                        </div>
                        {industriesOpen && (
                            <div className="flex flex-col pl-4 space-y-4">
                                {industriesList.map((item, idx) => (
                                    <Link
                                        to={`/industries/${slugify(item)}`}
                                        key={idx}
                                        className={`py-0 ${subLinkTextClasses}`}
                                        onClick={handleResultClick}
                                    >
                                        {item}
                                    </Link>
                                ))}
                            </div>
                        )}

                        <div
                            className={`flex justify-between items-center cursor-pointer py-1`}
                            onClick={() => {
                                setgameCategoriesOpen((prev) => !prev);
                                setIndustriesOpen(false);
                            }}
                        >
                            <span className={mainLinkTextClasses}>Game Categories</span>
                            <img
                                src={gameCategoriesOpen ? "/uploads/Assets/uparrowblack.svg" : "/uploads/Assets/Downarrowblack.svg"}
                                alt="Arrow"
                                className="h-3 w-3"
                            />
                        </div>
                        {gameCategoriesOpen && (
                            <div className="flex flex-col pl-4 space-y-4">
                                {gameCategoriesList.map((item, idx) => (
                                    <Link
                                        to={`/games/${slugify(item)}`}
                                        key={idx}
                                        className={`py-0 ${subLinkTextClasses}`}
                                        onClick={handleResultClick}
                                    >
                                        {item}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {mobileSearchOpen && (
                    <div
                        ref={mobileSearchRef}
                        className="xl-custom:hidden fixed top-[64px] left-0 w-full bottom-0 transition-all duration-300 z-40 bg-black py-4 px-4 overflow-y-auto"
                    >
                        <div className="mx-auto max-w-lg relative">
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleMobileSearchChange}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                className={mobileInputClasses}
                            />
                            <img
                                src="/uploads/Assets/Search icon.svg" 
                                alt="Search"
                                className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-auto"
                            />

                            {searchResults.length > 0 && (
                                <div 
                                    className="mt-4 w-full bg-white text-gray-600 shadow-lg rounded-md border border-gray-200 overflow-y-auto max-h-[75vh]"
                                    ref={mobileResultsRef} 
                                >
                                    <p className="px-4 py-2 text-xs font-bold uppercase text-gray-500 border-b border-gray-100 font-uber">
                                        Search Results ({searchResults.length})
                                    </p>
                                    {searchResults.map((result, idx) => (
                                        <Link
                                            to={result.url}
                                            key={result.key}
                                            className={`block px-4 py-2 transition duration-150 font-uber ${
                                                focusedIndex === idx 
                                                    ? 'bg-gray-100 text-black' 
                                                    : 'hover:bg-gray-100 hover:text-black' 
                                            }`}
                                            onClick={handleResultClick} 
                                        >
                                            <div className="font-uber font-medium text-sm leading-4 text-black">
                                                {highlightMatch(result.name, searchTerm)}
                                            </div>
                                            <div className="text-xs text-gray-500 font-uber">
                                                {result.type} 
                                                {(result.type === 'Brand' || result.type === 'Playable') && result.subtitle && (
                                                    <span className="ml-1 font-normal text-gray-400 font-uber">({result.subtitle})</span>
                                                )}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                            {searchResults.length === 0 && searchTerm.length > 0 && (
                                <div className="mt-4 w-full bg-white text-gray-600 shadow-lg rounded-md p-4 border border-gray-200">
                                    <p className="text-sm text-gray-500 font-uber">No results found for "{searchTerm}".</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </nav>
        </>
    );
}